/**
 * The lane's single feed, and the one place that decides how an entry reads.
 *
 * <h3>Why this is a module and not a useMemo</h3>
 *
 * Two surfaces render this feed — the wizard's lane and the build trail that
 * replaces it — and both have to agree on what a given event means. Held inline
 * in a page, the mapping gets copied the first time a second surface needs it,
 * and the two copies disagree the first time either is edited. There is one
 * answer here and every caller reads it from this file.
 *
 * <h3>Refused, and needs-you, are not the same thing</h3>
 *
 * This used to be one flag derived from the event level alone:
 *
 *   refused: e.level === 'warn' || e.eventType === 'run_failed'
 *
 * The pipeline emits {@code gap_detected} at warn level
 * ({@code useAutoCreateRun.js:92}), so a step waiting on a human decision
 * arrived in the lane as "Refused — s1 · no confident match", in deny red.
 * Nothing had been refused: the matcher found three candidates it was not
 * confident enough to choose between, and surfaced them for a person — which is
 * precisely what the surface promises it will do when it cannot work something
 * out.
 *
 * The two states call for opposite responses from whoever is reading. A refusal
 * is finished and says the boundary held. A gap is unfinished and says the build
 * is standing still until you answer. They cannot share a treatment, and they
 * must not share the word "Refused" — that word is load-bearing everywhere else
 * on this surface, and spending it on a non-event is how it stops being believed.
 *
 * Only {@code gap_detected} is reclassified here. Other warn-level events keep
 * the treatment they had: this is a correction to one known-wrong case, not a
 * licence to re-rank events nobody has read. {@code run_requires_review} is the
 * obvious next candidate and is deliberately left alone until its level is read.
 */

/**
 * Pipeline events that are waiting on a person rather than reporting a verdict.
 * Keyed by eventType, never by level — the level says how loud, not what kind.
 */
const AWAITING_PERSON = new Set(['gap_detected']);

/** Seconds since the run's first event, as the lane shows them. */
export function formatElapsed(at, firstAt) {
  if (!at || !firstAt) return '';
  const secs = Math.max(0, Math.round((new Date(at) - new Date(firstAt)) / 1000));
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

/**
 * One streamed pipeline event, as the lane reads it.
 *
 * @param {Object} e      `{ sequence, level, phase, message, payload, occurredAt, eventType }`
 * @param {string} firstAt the first streamed event's timestamp, for elapsed
 */
export function toLaneEntry(e, firstAt) {
  const needsYou = AWAITING_PERSON.has(e.eventType);
  const refused = needsYou ? false : (e.level === 'warn' || e.eventType === 'run_failed');
  return {
    id: e.sequence,
    at: formatElapsed(e.occurredAt, firstAt),
    /* The trail draws a node per step: a title in the step's own words and a
       quieter line saying what it means. The stream gives one message, so it is
       the title until a producer sends both. */
    title: e.message,
    text: null,
    detail: detailOf(e),
    tone: refused ? 'deny' : needsYou ? 'hold' : null,
    // A step that needs the visitor is never also a refusal, whatever its level.
    refused,
    needsYou,
    stepId: e.payload?.stepId || null,
  };
}

/**
 * The evidence a step produced, indented beneath it.
 *
 * Only shapes we have read are unpacked. A payload key nobody has seen is left
 * alone rather than guessed at — an invented line under a governed step is
 * worse than no line.
 */
function detailOf(e) {
  const p = e.payload;
  if (!p) return null;
  if (Array.isArray(p.steps)) {
    return p.steps
      .map((s) => [s.stepId, s.kind, s.name].filter(Boolean).join(' · '))
      .filter(Boolean);
  }
  if (Array.isArray(p.candidates)) {
    return p.candidates
      .map((c) => [c.registryId, c.confidence != null
        ? `${Math.round(c.confidence * 100)}%` : null].filter(Boolean).join(' · '))
      .filter(Boolean);
  }
  return null;
}

/**
 * Client-observed steps first, then the streamed ones.
 *
 * That order is chronological rather than arbitrary: the client steps are the
 * setup that had to finish before a run could start at all, so a visitor reads
 * down the lane and sees what happened in the order it happened.
 *
 * @param {Array} localSteps `[{ id, at, text, refused }]` emitted by the client
 * @param {Array} streamed   raw events from the run's stream, oldest first
 */
export function toLaneFeed(localSteps = [], streamed = []) {
  const firstAt = streamed[0]?.occurredAt;
  return [
    ...localSteps.map((s) => ({ needsYou: false, ...s })),
    ...streamed.map((e) => toLaneEntry(e, firstAt)),
  ];
}

export default toLaneFeed;
